import React from 'react';
import { Button, Card, Form, Input, InputNumber, Popconfirm, Space, Statistic, Table } from 'antd';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';

import { useIsAdmin } from '../../hooks/useIsAdmin';
import {
  loadAdminReferrals,
  recordAdminPayment,
  updateReferralSettings,
  deleteAdminPayment,
  recordAdminPayout,
  deleteAdminPayout,
  type AdminReferralPayment,
} from './reducer';
import { selectAdminReferralsLoading, selectAdminReferralsOverview } from './selectors';

type AdminReferralsPageProps = {
  embedded?: boolean;
  userLinkBase?: string;
};

const AdminReferralsPage = React.memo(
  ({ embedded = false, userLinkBase }: AdminReferralsPageProps) => {
    useIsAdmin();
    const resolvedUserLinkBase = userLinkBase ?? '/users';
    const dispatch = useDispatch();
    const overview = useSelector(selectAdminReferralsOverview);
    const loading = useSelector(selectAdminReferralsLoading);

    const [defaultPercent, setDefaultPercent] = React.useState<number>(10);
    const [payerEmail, setPayerEmail] = React.useState('');
    const [referrerEmail, setReferrerEmail] = React.useState('');
    const [amount, setAmount] = React.useState('');
    const [paymentPercent, setPaymentPercent] = React.useState<number>(10);
    const [note, setNote] = React.useState('');
    const [payoutReferrerEmail, setPayoutReferrerEmail] = React.useState('');
    const [payoutAmount, setPayoutAmount] = React.useState('');
    const [payoutNote, setPayoutNote] = React.useState('');

    React.useEffect(() => {
      dispatch(loadAdminReferrals());
    }, [dispatch]);

    React.useEffect(() => {
      if (overview?.referralPercent !== undefined) {
        setDefaultPercent(overview.referralPercent);
        setPaymentPercent(overview.referralPercent);
      }
    }, [overview?.referralPercent]);

    const handleSaveDefaultPercent = React.useCallback(() => {
      dispatch(updateReferralSettings(defaultPercent));
    }, [dispatch, defaultPercent]);

    const handleRecordPayment = React.useCallback(() => {
      const parsedAmount = parseFloat(amount.replace(',', '.'));
      if (!payerEmail.trim() || !parsedAmount || parsedAmount <= 0) {
        return;
      }

      dispatch(
        recordAdminPayment({
          payerEmail: payerEmail.trim(),
          amount: parsedAmount,
          commissionPercent: paymentPercent,
          referrerEmail: referrerEmail.trim() || undefined,
          note: note.trim() || undefined,
        }),
      );
      setAmount('');
      setNote('');
    }, [dispatch, payerEmail, referrerEmail, amount, paymentPercent, note]);

    const handleDeletePayment = React.useCallback(
      (paymentId: number) => {
        dispatch(deleteAdminPayment(paymentId));
      },
      [dispatch],
    );

    const handleRecordPayout = React.useCallback(() => {
      const parsedAmount = parseFloat(payoutAmount.replace(',', '.'));
      if (!payoutReferrerEmail.trim() || !parsedAmount || parsedAmount <= 0) {
        return;
      }

      dispatch(
        recordAdminPayout({
          referrerEmail: payoutReferrerEmail.trim(),
          amount: parsedAmount,
          note: payoutNote.trim() || undefined,
        }),
      );
      setPayoutAmount('');
      setPayoutNote('');
    }, [dispatch, payoutReferrerEmail, payoutAmount, payoutNote]);

    const handleDeletePayout = React.useCallback(
      (payoutId: number) => {
        dispatch(deleteAdminPayout(payoutId));
      },
      [dispatch],
    );

    const formatMoney = React.useCallback((value: number) => `$${Number(value).toFixed(2)}`, []);

    const userHref = React.useCallback(
      (id: number) => `${resolvedUserLinkBase}/${id}`,
      [resolvedUserLinkBase],
    );

    const referrerBalanceColumns = React.useMemo(
      () => [
        {
          title: 'Реферер',
          dataIndex: 'referrerEmail',
          render: (value: string, row: { referrerId: number }) => (
            <Link className="v2-admin-user-link" to={userHref(row.referrerId)}>
              {value}
            </Link>
          ),
        },
        { title: 'Рефералов', dataIndex: 'referralsCount' },
        { title: 'Начислено', dataIndex: 'totalEarned', render: formatMoney },
        { title: 'Выплачено', dataIndex: 'totalPaidOut', render: formatMoney },
        {
          title: 'К выплате',
          dataIndex: 'balance',
          render: (value: number) => (
            <span className="v2-admin-money v2-admin-money--highlight">
              {formatMoney(Number(value))}
            </span>
          ),
        },
      ],
      [formatMoney, userHref],
    );

    const paymentsColumns = React.useMemo(
      () => [
        {
          title: 'Дата',
          dataIndex: 'createdAt',
          render: (value: string) => dayjs(value).format('DD.MM.YY HH:mm'),
        },
        {
          title: 'Реферер',
          dataIndex: 'referrerEmail',
          render: (value: string, row: AdminReferralPayment) =>
            value ? (
              row.referrerId ? (
                <Link className="v2-admin-user-link" to={userHref(row.referrerId)}>
                  {value}
                </Link>
              ) : (
                value
              )
            ) : (
              '—'
            ),
        },
        {
          title: 'Реферал',
          dataIndex: 'payerEmail',
          render: (value: string, row: { payerId: number }) => (
            <Link className="v2-admin-user-link" to={userHref(row.payerId)}>
              {value}
            </Link>
          ),
        },
        {
          title: 'Сумма',
          dataIndex: 'amount',
          render: (value: number) => formatMoney(Number(value)),
        },
        {
          title: '%',
          dataIndex: 'commissionPercent',
          render: (value: number) => `${Number(value)}%`,
        },
        {
          title: 'Комиссия',
          dataIndex: 'commissionAmount',
          render: (value: number) => formatMoney(Number(value)),
        },
        {
          title: 'Примечание',
          dataIndex: 'note',
          render: (value?: string) => value || '—',
        },
        {
          title: '',
          key: 'actions',
          width: 96,
          render: (_value: unknown, row: { id: number }) => (
            <Popconfirm
              title="Удалить эту оплату?"
              description="Сумма и комиссия будут пересчитаны."
              okText="Удалить"
              cancelText="Отмена"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDeletePayment(row.id)}
            >
              <button type="button" className="v2-admin-link-danger">
                Удалить
              </button>
            </Popconfirm>
          ),
        },
      ],
      [formatMoney, handleDeletePayment, userHref],
    );

    const payoutsColumns = React.useMemo(
      () => [
        {
          title: 'Дата',
          dataIndex: 'createdAt',
          render: (value: string) => dayjs(value).format('DD.MM.YY HH:mm'),
        },
        {
          title: 'Реферер',
          dataIndex: 'referrerEmail',
          render: (value: string, row: { referrerId: number }) => (
            <Link className="v2-admin-user-link" to={userHref(row.referrerId)}>
              {value}
            </Link>
          ),
        },
        {
          title: 'Сумма',
          dataIndex: 'amount',
          render: formatMoney,
        },
        {
          title: 'Примечание',
          dataIndex: 'note',
          render: (value?: string) => value || '—',
        },
        {
          title: '',
          key: 'actions',
          width: 96,
          render: (_value: unknown, row: { id: number }) => (
            <Popconfirm
              title="Удалить эту выплату?"
              description="Баланс реферера будет пересчитан."
              okText="Удалить"
              cancelText="Отмена"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDeletePayout(row.id)}
            >
              <button type="button" className="v2-admin-link-danger">
                Удалить
              </button>
            </Popconfirm>
          ),
        },
      ],
      [formatMoney, handleDeletePayout, userHref],
    );

    const pendingColumns = React.useMemo(
      () => [
        {
          title: 'Реферер',
          dataIndex: 'referrerEmail',
          render: (value: string, row: { referrerId: number }) => (
            <Link className="v2-admin-user-link" to={userHref(row.referrerId)}>
              {value}
            </Link>
          ),
        },
        {
          title: 'Реферал',
          dataIndex: 'email',
          render: (value: string, row: { userId: number }) => (
            <Link className="v2-admin-user-link" to={userHref(row.userId)}>
              {value}
            </Link>
          ),
        },
        { title: 'Оплат', render: () => '0' },
        { title: 'Комиссия', render: () => '$0.00' },
      ],
      [userHref],
    );

    if (embedded) {
      return (
        <div className="v2-admin-referrals">
          <section className="kpi-grid" aria-label="Сводка по рефералам">
            <div className="kpi">
              <p className="kpi-label">Комиссия по умолчанию</p>
              <p className="kpi-value">
                {overview?.referralPercent ?? 0}
                <small>%</small>
              </p>
            </div>
            <div className="kpi">
              <p className="kpi-label">Всего оплат</p>
              <p className="kpi-value positive">{formatMoney(overview?.totalPaid ?? 0)}</p>
            </div>
            <div className="kpi">
              <p className="kpi-label">Всего комиссий</p>
              <p className="kpi-value">{formatMoney(overview?.totalCommission ?? 0)}</p>
            </div>
            <div className="kpi">
              <p className="kpi-label">Всего выплачено</p>
              <p className="kpi-value">{formatMoney(overview?.totalPaidOut ?? 0)}</p>
            </div>
            <div className="kpi">
              <p className="kpi-label">Без оплат</p>
              <p className="kpi-value">
                {overview?.pendingReferrals?.length ?? 0}
                <small>реф.</small>
              </p>
            </div>
          </section>

          <section className="settings-panel v2-admin-form-panel">
            <div className="settings-panel__head">
              <h2>Комиссия по умолчанию</h2>
            </div>
            <div className="v2-admin-inline-form">
              <label className="settings-field v2-admin-inline-form__field">
                <span className="settings-field-label">Процент</span>
                <InputNumber
                  min={0}
                  max={100}
                  step={0.5}
                  value={defaultPercent}
                  onChange={(value) => setDefaultPercent(Number(value ?? 10))}
                  addonAfter="%"
                />
              </label>
              <button type="button" className="acid-button" onClick={handleSaveDefaultPercent}>
                Сохранить
              </button>
            </div>
          </section>

          <section className="settings-panel v2-admin-form-panel">
            <div className="settings-panel__head">
              <h2>Записать оплату</h2>
            </div>
            <p className="settings-field-hint">
              Если реферер не был указан при регистрации — задайте email вручную.
            </p>
            <div className="v2-admin-payment-grid">
              <label className="settings-field">
                <span className="settings-field-label">Email пользователя</span>
                <Input
                  placeholder="user@example.com"
                  value={payerEmail}
                  onChange={(e) => setPayerEmail(e.target.value)}
                />
              </label>
              <label className="settings-field">
                <span className="settings-field-label">Email реферера</span>
                <Input
                  placeholder="referrer@example.com"
                  value={referrerEmail}
                  onChange={(e) => setReferrerEmail(e.target.value)}
                />
              </label>
              <label className="settings-field">
                <span className="settings-field-label">Сумма, $</span>
                <Input
                  placeholder="30"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </label>
              <label className="settings-field">
                <span className="settings-field-label">Комиссия, %</span>
                <InputNumber
                  min={0}
                  max={100}
                  step={0.5}
                  value={paymentPercent}
                  onChange={(value) => setPaymentPercent(Number(value ?? 10))}
                  style={{ width: '100%' }}
                />
              </label>
              <label className="settings-field v2-admin-payment-grid__wide">
                <span className="settings-field-label">Примечание</span>
                <Input
                  placeholder="Необязательно"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
            </div>
            <div className="settings-panel__footer">
              <button type="button" className="acid-button" onClick={handleRecordPayment}>
                Записать оплату
              </button>
            </div>
          </section>

          <section className="settings-panel v2-admin-form-panel">
            <div className="settings-panel__head">
              <h2>Записать выплату рефереру</h2>
            </div>
            <div className="v2-admin-payment-grid">
              <label className="settings-field">
                <span className="settings-field-label">Email реферера</span>
                <Input
                  placeholder="referrer@example.com"
                  value={payoutReferrerEmail}
                  onChange={(e) => setPayoutReferrerEmail(e.target.value)}
                />
              </label>
              <label className="settings-field">
                <span className="settings-field-label">Сумма выплаты, $</span>
                <Input
                  placeholder="50"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                />
              </label>
              <label className="settings-field v2-admin-payment-grid__wide">
                <span className="settings-field-label">Примечание</span>
                <Input
                  placeholder="Необязательно"
                  value={payoutNote}
                  onChange={(e) => setPayoutNote(e.target.value)}
                />
              </label>
            </div>
            <div className="settings-panel__footer">
              <button type="button" className="acid-button" onClick={handleRecordPayout}>
                Записать выплату
              </button>
            </div>
          </section>

          <section className="settings-panel v2-admin-table-panel">
            <div className="settings-panel__head">
              <h2>Баланс рефереров</h2>
            </div>
            <div className="table-scroll v2-admin-table-scroll">
              <Table
                loading={loading}
                rowKey="referrerId"
                size="small"
                dataSource={overview?.referrers ?? []}
                pagination={{ pageSize: 20, showSizeChanger: false }}
                columns={referrerBalanceColumns}
                scroll={{ x: 720 }}
              />
            </div>
          </section>

          <section className="settings-panel v2-admin-table-panel">
            <div className="settings-panel__head">
              <h2>Оплаты рефералов</h2>
            </div>
            <div className="table-scroll v2-admin-table-scroll">
              <Table
                loading={loading}
                rowKey="id"
                size="small"
                dataSource={overview?.payments ?? []}
                pagination={{ pageSize: 20, showSizeChanger: false }}
                columns={paymentsColumns}
                scroll={{ x: 980 }}
              />
            </div>
          </section>

          <section className="settings-panel v2-admin-table-panel">
            <div className="settings-panel__head">
              <h2>История выплат</h2>
            </div>
            <div className="table-scroll v2-admin-table-scroll">
              <Table
                loading={loading}
                rowKey="id"
                size="small"
                dataSource={overview?.payouts ?? []}
                pagination={{ pageSize: 20, showSizeChanger: false }}
                columns={payoutsColumns}
                scroll={{ x: 760 }}
              />
            </div>
          </section>

          <section className="settings-panel v2-admin-table-panel">
            <div className="settings-panel__head">
              <h2>Приглашённые без оплат</h2>
            </div>
            <div className="table-scroll v2-admin-table-scroll">
              <Table
                loading={loading}
                rowKey="userId"
                size="small"
                dataSource={overview?.pendingReferrals ?? []}
                pagination={{ pageSize: 20, showSizeChanger: false }}
                columns={pendingColumns}
                scroll={{ x: 640 }}
              />
            </div>
          </section>
        </div>
      );
    }

    return (
      <div style={{ margin: '32px auto', maxWidth: 1600 }}>
        <Card title="Рефералы" extra={<Link to="/users">К списку пользователей</Link>}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 16,
              marginBottom: 24,
            }}
          >
            <Statistic
              title="Комиссия по умолчанию"
              value={overview?.referralPercent ?? 0}
              suffix="%"
            />
            <Statistic
              title="Всего оплат"
              value={overview?.totalPaid ?? 0}
              prefix="$"
              precision={2}
            />
            <Statistic
              title="Всего комиссий"
              value={overview?.totalCommission ?? 0}
              prefix="$"
              precision={2}
            />
            <Statistic
              title="Всего выплачено"
              value={overview?.totalPaidOut ?? 0}
              prefix="$"
              precision={2}
            />
            <Statistic
              title="Рефералов без оплат"
              value={overview?.pendingReferrals?.length ?? 0}
            />
          </div>

          <Card type="inner" title="Комиссия по умолчанию" style={{ marginBottom: 24 }}>
            <Space wrap>
              <InputNumber
                min={0}
                max={100}
                step={0.5}
                value={defaultPercent}
                onChange={(value) => setDefaultPercent(Number(value ?? 10))}
                addonAfter="%"
              />
              <Button type="primary" onClick={handleSaveDefaultPercent}>
                Сохранить
              </Button>
            </Space>
          </Card>

          <Card type="inner" title="Записать оплату" style={{ marginBottom: 24 }}>
            <Form layout="vertical">
              <Space wrap align="start" style={{ width: '100%' }}>
                <Form.Item label="Email пользователя" style={{ minWidth: 260, marginBottom: 0 }}>
                  <Input
                    placeholder="user@example.com"
                    value={payerEmail}
                    onChange={(e) => setPayerEmail(e.target.value)}
                  />
                </Form.Item>
                <Form.Item
                  label="Email реферера"
                  style={{ minWidth: 260, marginBottom: 0 }}
                  extra="Если не указан при регистрации — укажите здесь"
                >
                  <Input
                    placeholder="referrer@example.com"
                    value={referrerEmail}
                    onChange={(e) => setReferrerEmail(e.target.value)}
                  />
                </Form.Item>
                <Form.Item label="Сумма, $" style={{ minWidth: 140, marginBottom: 0 }}>
                  <Input
                    placeholder="30"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </Form.Item>
                <Form.Item label="Комиссия, %" style={{ minWidth: 140, marginBottom: 0 }}>
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.5}
                    value={paymentPercent}
                    onChange={(value) => setPaymentPercent(Number(value ?? 10))}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item label="Примечание" style={{ minWidth: 240, marginBottom: 0 }}>
                  <Input
                    placeholder="Необязательно"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Form.Item>
                <Form.Item label=" " style={{ marginBottom: 0 }}>
                  <Button type="primary" onClick={handleRecordPayment}>
                    Записать
                  </Button>
                </Form.Item>
              </Space>
            </Form>
          </Card>

          <Card type="inner" title="Записать выплату рефереру" style={{ marginBottom: 24 }}>
            <Form layout="vertical">
              <Space wrap align="start" style={{ width: '100%' }}>
                <Form.Item label="Email реферера" style={{ minWidth: 260, marginBottom: 0 }}>
                  <Input
                    placeholder="referrer@example.com"
                    value={payoutReferrerEmail}
                    onChange={(e) => setPayoutReferrerEmail(e.target.value)}
                  />
                </Form.Item>
                <Form.Item label="Сумма выплаты, $" style={{ minWidth: 160, marginBottom: 0 }}>
                  <Input
                    placeholder="50"
                    value={payoutAmount}
                    onChange={(e) => setPayoutAmount(e.target.value)}
                  />
                </Form.Item>
                <Form.Item label="Примечание" style={{ minWidth: 240, marginBottom: 0 }}>
                  <Input
                    placeholder="Необязательно"
                    value={payoutNote}
                    onChange={(e) => setPayoutNote(e.target.value)}
                  />
                </Form.Item>
                <Form.Item label=" " style={{ marginBottom: 0 }}>
                  <Button type="primary" onClick={handleRecordPayout}>
                    Записать выплату
                  </Button>
                </Form.Item>
              </Space>
            </Form>
          </Card>

          <Table
            loading={loading}
            rowKey="referrerId"
            size="small"
            style={{ marginBottom: 24 }}
            title={() => 'Баланс рефереров'}
            dataSource={overview?.referrers ?? []}
            pagination={{ pageSize: 20 }}
            columns={referrerBalanceColumns}
          />

          <Table
            loading={loading}
            rowKey="id"
            size="small"
            title={() => 'Оплаты рефералов'}
            dataSource={overview?.payments ?? []}
            pagination={{ pageSize: 20 }}
            columns={paymentsColumns}
          />

          <Table
            loading={loading}
            rowKey="id"
            size="small"
            style={{ marginTop: 24 }}
            title={() => 'История выплат реферерам'}
            dataSource={overview?.payouts ?? []}
            pagination={{ pageSize: 20 }}
            columns={payoutsColumns}
          />

          <Table
            loading={loading}
            rowKey="userId"
            size="small"
            style={{ marginTop: 24 }}
            title={() => 'Приглашённые без оплат'}
            dataSource={overview?.pendingReferrals ?? []}
            pagination={{ pageSize: 20 }}
            columns={pendingColumns}
          />
        </Card>
      </div>
    );
  },
);

export default AdminReferralsPage;
